'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useAuth } from '@/components/providers/auth-provider'
import { useRouter } from 'next/navigation'
import { BrandLogo } from '@/components/landing/BrandLogo'

// ── Rotating gradient slides ──────────────────────────────────────
const SLIDES = [
  { from: '#F9C8C8', via: '#F5E0E0', to: '#EBEBEB' },
  { from: '#E8D5E8', via: '#F0E5F0', to: '#EBEBEB' },
  { from: '#D5E5F0', via: '#E5EFF5', to: '#EBEBEB' },
  { from: '#F0E5D5', via: '#F5EDE0', to: '#EBEBEB' },
  { from: '#E0F0E5', via: '#EAF5EE', to: '#EBEBEB' },
  { from: '#F9C8C8', via: '#F0D8D8', to: '#EBEBEB' },
]

function AnimatedHero({ children }: { children: React.ReactNode }) {
  const [slide, setSlide] = useState(0)

  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % SLIDES.length), 5000)
    return () => clearInterval(t)
  }, [])

  return (
    <div className="relative w-full overflow-hidden" style={{ minHeight: '100vh' }}>
      {/* Rotating gradient slides */}
      {SLIDES.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-[2000ms] ease-in-out"
          style={{
            opacity: i === slide ? 1 : 0,
            background: `linear-gradient(135deg, ${s.from} 0%, ${s.via} 50%, ${s.to} 100%)`,
          }}
        />
      ))}

      {/* Floating shapes */}
      <div className="absolute top-[8%] right-[8%] w-72 h-72 rounded-full opacity-30 animate-pulse" style={{ background: '#F9C8C8', filter: 'blur(80px)' }} />
      <div className="absolute bottom-[12%] left-[6%] w-96 h-96 rounded-full opacity-25 animate-pulse" style={{ background: '#F9C8C8', filter: 'blur(100px)', animationDelay: '2s' }} />
      <div className="absolute top-[40%] left-[40%] w-64 h-64 rounded-full opacity-20" style={{ background: '#F9C8C8', filter: 'blur(120px)', animation: 'floatGentle 8s ease-in-out infinite' }} />

      {/* Content */}
      <div className="relative z-10">{children}</div>

      <style jsx>{`
        @keyframes floatGentle {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, -30px); }
        }
      `}</style>
    </div>
  )
}

// ── Site header (top nav) ─────────────────────────────────────────
function SiteHeader({ lang = 'et' }: { lang?: 'et' | 'en' }) {
  const t = lang === 'et'
    ? { signin: 'Sisene', applyTip: 'Esita taotlus', langTip: 'Vaheta keelt' }
    : { signin: 'Sign in', applyTip: 'Submit application', langTip: 'Change language' }

  const [langOpen, setLangOpen] = useState(false)
  const langRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click + Escape
  useEffect(() => {
    if (!langOpen) return
    const onDown = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLangOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [langOpen])

  return (
    <header className="absolute top-0 left-0 right-0 z-20 px-6 md:px-12 py-6 flex items-center justify-between">
      <Link href={lang === 'et' ? '/' : '/en'} className="flex items-center gap-3">
        <BrandLogo />
        <span className="text-[15px] font-semibold tracking-tight" style={{ color: '#111' }}>Sassu.io</span>
      </Link>
      <nav className="flex items-center gap-1 md:gap-2">
        {/* Mail icon → application form */}
        <Link
          href={lang === 'et' ? '/taotlus' : '/en/apply'}
          aria-label={t.applyTip}
          title={t.applyTip}
          className="group relative inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors hover:bg-black/5"
          style={{ color: '#111' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
          <Tooltip text={t.applyTip} />
        </Link>

        {/* Globe icon → language dropdown */}
        <div ref={langRef} className="relative">
          <button
            type="button"
            onClick={() => setLangOpen(o => !o)}
            aria-label={t.langTip}
            title={t.langTip}
            className="group relative inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors hover:bg-black/5"
            style={{ color: '#111' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <Tooltip text={t.langTip} />
          </button>

          {langOpen && (
            <div
              className="absolute right-0 top-12 min-w-[120px] rounded-xl bg-white shadow-xl border border-black/5 overflow-hidden"
              style={{ animation: 'dropdownIn 150ms ease-out' }}
            >
              <Link
                href="/"
                onClick={() => setLangOpen(false)}
                className={`flex items-center justify-between px-4 py-2.5 text-[13px] font-medium transition-colors hover:bg-black/5 ${lang === 'et' ? 'text-black' : 'text-gray-500'}`}
              >
                <span>EST</span>
                {lang === 'et' && <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#F9C8C8' }} />}
              </Link>
              <Link
                href="/en"
                onClick={() => setLangOpen(false)}
                className={`flex items-center justify-between px-4 py-2.5 text-[13px] font-medium transition-colors hover:bg-black/5 ${lang === 'en' ? 'text-black' : 'text-gray-500'}`}
              >
                <span>ENG</span>
                {lang === 'en' && <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#F9C8C8' }} />}
              </Link>
              <style jsx>{`
                @keyframes dropdownIn {
                  from { opacity: 0; transform: translateY(-4px); }
                  to { opacity: 1; transform: translateY(0); }
                }
              `}</style>
            </div>
          )}
        </div>

        {/* Sign in pill */}
        <Link href="/sisene" className="ml-1 md:ml-2 inline-flex items-center px-4 py-2 rounded-full text-[13px] font-medium transition-colors" style={{ background: '#111', color: '#fff' }}>
          {t.signin}
        </Link>
      </nav>
    </header>
  )
}

// Tooltip helper
function Tooltip({ text }: { text: string }) {
  return (
    <span
      className="pointer-events-none absolute top-12 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-black/85 px-2.5 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100"
      style={{ backdropFilter: 'blur(8px)' }}
    >
      {text}
    </span>
  )
}

// ── Footer ────────────────────────────────────────────────────────
function SiteFooter({ lang = 'et' }: { lang?: 'et' | 'en' }) {
  const t = lang === 'et' ? {
    company: 'NG OÜ',
    regCode: 'Reg.kood',
    address: 'Aadress',
    email: 'E-post',
    phone: 'Telefon',
    privacy: 'Privaatsuspoliitika',
    terms: 'Tingimused',
    signin: 'Sisene',
    apply: 'Esita taotlus',
    rights: 'Kõik õigused kaitstud.',
    language: 'Keel',
  } : {
    company: 'NG OÜ',
    regCode: 'Reg. code',
    address: 'Address',
    email: 'Email',
    phone: 'Phone',
    privacy: 'Privacy Policy',
    terms: 'Terms',
    signin: 'Sign in',
    apply: 'Apply',
    rights: 'All rights reserved.',
    language: 'Language',
  }
  const year = 2026

  return (
    <footer className="bg-white border-t border-black/5 py-16 px-6 md:px-12">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <BrandLogo />
            <span className="text-[15px] font-semibold tracking-tight" style={{ color: '#111' }}>Sassu.io</span>
          </div>
          <p className="text-[13px] text-gray-500 leading-relaxed">
            {lang === 'et'
              ? 'Arve- ja kliendihalduse SaaS valitud seltskonnale.'
              : 'Invoicing and customer management SaaS for a curated audience.'}
          </p>
        </div>

        {/* Company info */}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-3">{t.company}</div>
          <div className="text-[13px] text-gray-600 leading-relaxed space-y-1">
            <div>{t.regCode}: 12954414</div>
            <div>Kentmanni tn 11b-7</div>
            <div>10116 Tallinn, Estonia</div>
            <div className="pt-2 text-gray-400 text-[12px]">
              {lang === 'et'
                ? 'Kontakt ainult vormi kaudu.'
                : 'Contact through application form only.'}
            </div>
          </div>
        </div>

        {/* Links */}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-3">{lang === 'et' ? 'Lingid' : 'Links'}</div>
          <div className="text-[13px] text-gray-600 leading-relaxed space-y-1">
            <div><Link href={lang === 'et' ? '/taotlus' : '/en/apply'} className="hover:text-black transition-colors">{t.apply}</Link></div>
            <div><Link href="/sisene" className="hover:text-black transition-colors">{t.signin}</Link></div>
            <div><Link href={lang === 'et' ? '/privaatsus' : '/en/privacy'} className="hover:text-black transition-colors">{t.privacy}</Link></div>
            <div><Link href={lang === 'et' ? '/tingimused' : '/en/terms'} className="hover:text-black transition-colors">{t.terms}</Link></div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto mt-12 pt-8 border-t border-black/5 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="text-[12px] text-gray-400">© {year} NG OÜ. {t.rights}</div>
        <div className="flex items-center gap-3 text-[12px]">
          <span className="text-gray-400">{t.language}:</span>
          <Link href="/" className={lang === 'et' ? 'font-semibold text-black' : 'text-gray-400 hover:text-black'}>EST</Link>
          <span className="text-gray-300">·</span>
          <Link href="/en" className={lang === 'en' ? 'font-semibold text-black' : 'text-gray-400 hover:text-black'}>ENG</Link>
        </div>
      </div>
    </footer>
  )
}

export { SiteHeader, SiteFooter, AnimatedHero }

// ── Main landing page (Estonian) ──────────────────────────────────
export default function LandingPage() {
  const { user, loading } = useAuth()
  const router = useRouter()

  // If already logged in, redirect to dashboard (silent)
  useEffect(() => {
    if (!loading && user) {
      router.replace('/dashboard/invoices/quick')
    }
  }, [user, loading, router])

  return (
    <div className="bg-[#EBEBEB] min-h-screen" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* HERO */}
      <AnimatedHero>
        <SiteHeader lang="et" />

        <div className="flex items-center justify-center min-h-screen px-6 md:px-12 pt-32 pb-20">
          <div className="max-w-3xl text-center">
            <div className="inline-block mb-8 px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ background: '#111', color: '#fff' }}>
              Liitumine kutsel
            </div>
            <h1 className="text-[44px] md:text-[72px] leading-[1.05] mb-8 tracking-tight" style={{ fontWeight: 300, color: '#111', letterSpacing: '-0.02em' }}>
              Arve- ja kliendihaldus<br /><strong style={{ fontWeight: 700 }}>valitud seltskonnale</strong>
            </h1>
            <p className="text-[17px] md:text-[20px] text-gray-700 max-w-2xl mx-auto leading-relaxed mb-10" style={{ fontWeight: 300 }}>
              Loodud Eesti väikeettevõtjatele, kes väärtustavad lihtsust, kvaliteeti ja personaalset teenust. Iga kasutaja võtame vastu isiklikult.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link href="/taotlus" className="inline-flex items-center px-7 py-3.5 rounded-full text-[14px] font-semibold transition-all hover:scale-[1.02]" style={{ background: '#111', color: '#fff' }}>
                Esita taotlus →
              </Link>
              <Link href="/sisene" className="inline-flex items-center px-7 py-3.5 rounded-full text-[14px] font-medium transition-colors hover:bg-black/5" style={{ color: '#111' }}>
                Mul on juba konto
              </Link>
            </div>
          </div>
        </div>
      </AnimatedHero>

      {/* WHAT WE DO */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-4">Mida me teeme</div>
          <h2 className="text-[36px] md:text-[56px] leading-[1.1] mb-16 tracking-tight" style={{ fontWeight: 300, color: '#111', letterSpacing: '-0.02em' }}>
            Lihtne, vaikselt<br /><strong style={{ fontWeight: 700 }}>tehtud korralikult.</strong>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'Arvete koostamine', text: 'Kiire arvete koostamine, klientide ja teenuste haldus ühes kohas. Pangaülekanded sobitatakse arvetega automaatselt.' },
              { title: 'Korduvarved', text: 'Igakuised teenused arvelduvad ise. Sina kontrollid ja tehnoloogia teeb monotoonse töö ära.' },
              { title: 'Klientide lood', text: 'Mitte tabel, vaid suhted. Iga kliendi juures on kogu tema lugu — arved, makseajalugu, märkmed.' },
              { title: 'Aruanded ja jälgimine', text: 'Selge pilt sinu äri tervisest. Sularaha sissetulek, võlglased, prognoosid — alati üks klikk eemal.' },
            ].map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 md:p-10">
                <div className="w-10 h-10 rounded-full mb-6 flex items-center justify-center" style={{ background: '#F9C8C8' }}>
                  <span style={{ color: '#111', fontWeight: 700, fontSize: '14px' }}>{i + 1}</span>
                </div>
                <h3 className="text-[20px] font-semibold mb-3 tracking-tight" style={{ color: '#111' }}>{f.title}</h3>
                <p className="text-[15px] text-gray-600 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR WHO */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-4">Kellele</div>
          <h2 className="text-[36px] md:text-[56px] leading-[1.1] mb-10 tracking-tight" style={{ fontWeight: 300, color: '#111', letterSpacing: '-0.02em' }}>
            Mitte<br /><strong style={{ fontWeight: 700 }}>kõigile.</strong>
          </h2>
          <p className="text-[17px] md:text-[20px] text-gray-600 leading-relaxed mb-6" style={{ fontWeight: 300 }}>
            Sassu pole massitoode. Iga kasutaja võtame vastu isiklikult, et tagada, et meie teenus sobib päriselt sinu vajadustega.
          </p>
          <p className="text-[17px] md:text-[20px] text-gray-600 leading-relaxed mb-12" style={{ fontWeight: 300 }}>
            Kui sa oled väikeettevõtja, kelle aeg on väärt rohkem kui odavaim alternatiiv — võta meiega ühendust.
          </p>
          <Link href="/taotlus" className="inline-flex items-center px-7 py-3.5 rounded-full text-[14px] font-semibold transition-all hover:scale-[1.02]" style={{ background: '#F9C8C8', color: '#111' }}>
            Räägi meile endast →
          </Link>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-24 md:py-32 px-6 md:px-12" style={{ background: '#EBEBEB' }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-4">Liitumine</div>
          <h2 className="text-[36px] md:text-[56px] leading-[1.1] mb-8 tracking-tight" style={{ fontWeight: 300, color: '#111', letterSpacing: '-0.02em' }}>
            Üks vorm —<br /><strong style={{ fontWeight: 700 }}>üks vestlus.</strong>
          </h2>
          <p className="text-[16px] md:text-[18px] text-gray-600 leading-relaxed mb-10 max-w-xl mx-auto" style={{ fontWeight: 300 }}>
            Me ei avalda telefoninumbreid ega e-posti aadresse. Kogu suhtlus algab läbi taotluse vormi —
            nii saame õiged inimesed õigel ajal kokku viia.
          </p>
          <Link href="/taotlus" className="inline-flex items-center px-7 py-3.5 rounded-full text-[14px] font-semibold transition-all hover:scale-[1.02]" style={{ background: '#111', color: '#fff' }}>
            Esita liitumistaotlus →
          </Link>
        </div>
      </section>

      <SiteFooter lang="et" />
    </div>
  )
}
